/**
 * Remembers the text size the teacher last set in the read-aloud modal, so a
 * class that needs bigger print doesn't have to enlarge it for every passage.
 *
 * Sibling of readAloudModalWidthStorage — same shape, separate key, and the
 * clamping has nothing in common (width depends on the viewport, this doesn't).
 */

export const READ_ALOUD_FONT_SIZE_KEY = 'curipod_read_aloud_font_size';

/** Unchanged from the size the modal has always opened at. */
export const DEFAULT_READ_ALOUD_FONT_SIZE = 24;

/** How much one press of the A+ / A- button moves it. */
export const READ_ALOUD_FONT_SIZE_STEP = 2;

/**
 * The controls were previously unbounded, which was survivable because the size
 * reset every time the modal closed. Now that it persists, a teacher who holds
 * A- past zero would carry an unreadable passage into every lesson with no hint
 * as to why, so the range is capped at both ends.
 */
export const MIN_READ_ALOUD_FONT_SIZE = 12;
export const MAX_READ_ALOUD_FONT_SIZE = 72;

export function clampReadAloudFontSize(size: number): number {
  if (!Number.isFinite(size)) return DEFAULT_READ_ALOUD_FONT_SIZE;
  return Math.round(
    Math.min(Math.max(size, MIN_READ_ALOUD_FONT_SIZE), MAX_READ_ALOUD_FONT_SIZE),
  );
}

export function readStoredReadAloudFontSize(): number {
  if (typeof window === 'undefined') return DEFAULT_READ_ALOUD_FONT_SIZE;

  try {
    const raw = window.localStorage.getItem(READ_ALOUD_FONT_SIZE_KEY);
    const parsed = raw === null ? NaN : Number(raw);
    return clampReadAloudFontSize(
      Number.isFinite(parsed) ? parsed : DEFAULT_READ_ALOUD_FONT_SIZE,
    );
  } catch {
    // Unparseable or blocked storage.
    return DEFAULT_READ_ALOUD_FONT_SIZE;
  }
}

export function writeStoredReadAloudFontSize(size: number) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(READ_ALOUD_FONT_SIZE_KEY, String(Math.round(size)));
  } catch {
    // Storage full or blocked (private mode) — resizing the text still works,
    // it just won't be remembered next time.
  }
}
